import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";
import { usersApi } from "@/slices/users/usersApi";

export const personsApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "personsApi",
    baseUrl: "/api/persons",
  }),
  reducerPath: "personsApi",
  tagTypes: ["Persons", "Person"],
  endpoints: (builder) => ({
    getPersons: builder.query<
      any,
      {
        search?: string;
        role?: string;
        quickFilter?: string;
        include?: string;
        limit?: number;
        nextId?: string;
        hasRelationship?: string;
        contactType?: string;
        companyId?: string;
        scope?: string;
        sort?: string;
        direction?: string;
      }
    >({
      query: (args) => {
        // Build query parameters dynamically
        const queryParams = new URLSearchParams();
        // Append parameters if they exist
        if (args.search) queryParams.append("query", args.search);
        if (args.role) queryParams.append("role", args.role);
        if (args.quickFilter)
          queryParams.append("QUICK_FILTER", args.quickFilter);
        if (args.include) queryParams.append("include", args.include);
        if (args.limit) queryParams.append("limit", args.limit.toString());
        if (args.nextId) queryParams.append("nextId", args.nextId);
        if (args.hasRelationship !== undefined) {
          queryParams.append(
            "hasRelationship",
            args.hasRelationship.toString()
          );
        }
        if (args.contactType)
          queryParams.append("contactType", args.contactType);
        if (args.companyId) queryParams.append("companyId", args.companyId);
        if (args.scope) queryParams.append("scope", args.scope);
        if (args.sort) queryParams.append("sort", args.sort);
        if (args.direction) queryParams.append("direction", args.direction);
        return `/search?${queryParams.toString()}`;
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Persons" }],
    }),
    getSuggestedPersons: builder.query<
      any,
      { role: string; limit?: number }
    >({
      query: ({ role, limit }) => {
        const queryParams = new URLSearchParams();
        queryParams.append("role", role);
        if (limit) queryParams.append("limit", limit.toString());
        return `/suggested?${queryParams.toString()}`;
      },
      providesTags: [{ type: "Persons" }],
    }),
    getPersonsCount: builder.query<{ data: number }, void>({
      query: () => `/count`,
    }),
    getPersonsCountByType: builder.query<
      { data: { myContacts: number; myCompany: number; affiliates: number; everyone: number } },
      { companyId: string }
    >({
      query: ({ companyId }) => `/count-by-type?companyId=${companyId}`,
      providesTags: [{ type: "Persons" }],
    }),
    // Real per-bucket counts over the whole book in the current tab scope
    // (not the paginated page). Powers the Contacts filter cards.
    getBucketCounts: builder.query<
      {
        data: {
          lead: number;
          referral: number;
          touchpoint: number;
          needs: number;
        };
      },
      { scope?: string; contactType?: string; companyId?: string }
    >({
      query: ({ scope, contactType, companyId }) => {
        const p = new URLSearchParams();
        if (scope) p.append("scope", scope);
        if (contactType) p.append("contactType", contactType);
        if (companyId) p.append("companyId", companyId);
        return `/bucket-counts?${p.toString()}`;
      },
      providesTags: [{ type: "Persons" }],
    }),
    getPersonById: builder.query({
      query: (id) => `/${id}`,
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Person" }],
    }),
    updatePersonById: builder.mutation<any, { id: string; personData: any }>({
      query: ({ id, personData }) => ({
        url: `/${id}`,
        method: "PUT",
        body: personData,
      }),
    }),
    createRelationship: builder.mutation<
      any,
      { name: string; email: string; phone?: string; isLead?: boolean }
    >({
      query: (relationship) => ({
        url: "/",
        method: "POST",
        body: relationship,
      }),
    }),
    // REP-226 — set the current user's explicit lead flag for a contact.
    // Invalidates the Person detail + the Persons list (bucket counts /
    // list rows) so the drawer and contact board reflect the new state.
    setPersonLead: builder.mutation<any, { id: string; isLead: boolean }>({
      query: ({ id, isLead }) => ({
        url: `/${id}/lead`,
        method: "PATCH",
        body: { isLead },
      }),
      invalidatesTags: [{ type: "Person" }, { type: "Persons" }],
    }),
    bulkImportPersons: builder.mutation<
      any,
      Array<{ name: string; email: string; phone?: string }>
    >({
      query: (persons) => ({
        url: "/bulk-import",
        method: "POST",
        body: persons,
      }),
      invalidatesTags: ["Person"],
    }),
    createPersonPhoneNumber: builder.mutation<
      any,
      { id: string; phoneNumber: string }
    >({
      query: ({ id, phoneNumber }) => ({
        url: `/${id}/phone`,
        method: "POST",
        body: { phoneNumber },
      }),
      invalidatesTags: [{ type: "Person" }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        // Phone also surfaces on the user-scoped contact-info cache.
        dispatch(usersApi.util.invalidateTags(["User"]));
      },
    }),
    updatePersonPhoneNumber: builder.mutation<
      any,
      { id: string; phoneId: string; phoneNumber: string }
    >({
      query: ({ id, phoneId, phoneNumber }) => ({
        url: `/${id}/phone/${phoneId}`,
        method: "PUT",
        body: { phoneNumber },
      }),
      invalidatesTags: [{ type: "Person" }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        // Phone also surfaces on the user-scoped contact-info cache.
        dispatch(usersApi.util.invalidateTags(["User"]));
      },
    }),
    deletePersonPhoneNumber: builder.mutation<
      any,
      { id: string; phoneId: string }
    >({
      query: ({ id, phoneId }) => ({
        url: `/${id}/phone/${phoneId}`,
        method: "DELETE",
      }),
    }),
    createPersonEmail: builder.mutation<
      any,
      { id: string; emailAddress: string }
    >({
      query: ({ id, emailAddress }) => ({
        url: `/${id}/email`,
        method: "POST",
        body: { emailAddress },
      }),
    }),
    updatePersonEmail: builder.mutation<
      any,
      { id: string; emailId: string; emailAddress: string }
    >({
      query: ({ id, emailId, emailAddress }) => ({
        url: `/${id}/email/${emailId}`,
        method: "PUT",
        body: { emailAddress },
      }),
    }),
    createPersonNote: builder.mutation<any, { id: string; text: string }>({
      query: ({ id, text }) => ({
        url: `/${id}/notes`,
        method: "POST",
        body: { text },
      }),
    }),
    getPersonNotes: builder.query({
      query: (id) => {
        return `/${id}/notes`;
      },
    }),
    updateNote: builder.mutation<
      any,
      { id: string; noteId: string; text: string }
    >({
      query: ({ id, noteId, text }) => ({
        url: `/${id}/notes/${noteId}`,
        method: "PUT",
        body: { text },
      }),
    }),
    deleteNote: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/notes/${id}`,
        method: "DELETE",
      }),
    }),
    createPersonRating: builder.mutation<
      any,
      { id: string; value: number; text: string }
    >({
      query: ({ id, value, text }) => ({
        url: `/${id}/ratings`,
        method: "POST",
        body: { value, text },
      }),
    }),
    updatePersonRating: builder.mutation<
      any,
      { id: string; ratingId: string; value: number; text: string }
    >({
      query: ({ id, ratingId, value, text }) => ({
        url: `/${id}/ratings/${ratingId}`,
        method: "PUT",
        body: { value, text },
      }),
    }),
    deleteRating: builder.mutation<any, { ratingId: string }>({
      query: ({ ratingId }) => ({
        url: `/ratings/${ratingId}`,
        method: "DELETE",
      }),
    }),
    deletePerson: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
    }),
    createPersonAddress: builder.mutation<
      any,
      {
        id: string;
        address: {
          addressLine1: string;
          addressLine2?: string;
          city: string;
          state: string;
          postalCode: string;
          country: string;
          primary?: boolean;
        };
      }
    >({
      query: ({ id, address }) => ({
        url: `/${id}/address`,
        method: "POST",
        body: address,
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    updatePersonAddress: builder.mutation<
      any,
      {
        id: string;
        addressId: string;
        address: {
          addressLine1: string;
          addressLine2?: string;
          city: string;
          state: string;
          postalCode: string;
          country: string;
          primary?: boolean;
        };
      }
    >({
      query: ({ id, addressId, address }) => ({
        url: `/${id}/address/${addressId}`,
        method: "PUT",
        body: address,
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    deletePersonAddress: builder.mutation<
      any,
      { id: string; addressId: string }
    >({
      query: ({ id, addressId }) => ({
        url: `/${id}/address/${addressId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    dismissPersonProfileBanner: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}/dismiss-profile-banner`,
        method: "PATCH",
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    // PersonOverride mutations
    updatePersonOverride: builder.mutation<
      any,
      { id: string; data: { name?: string; bio?: string; title?: string } }
    >({
      query: ({ id, data }) => ({
        url: `/${id}/override`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    addOverrideEmail: builder.mutation<
      any,
      { id: string; value: string; primary?: boolean }
    >({
      query: ({ id, value, primary }) => ({
        url: `/${id}/override/email`,
        method: "POST",
        body: { value, primary },
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    addOverridePhone: builder.mutation<
      any,
      { id: string; value: string; primary?: boolean }
    >({
      query: ({ id, value, primary }) => ({
        url: `/${id}/override/phone`,
        method: "POST",
        body: { value, primary },
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    deleteOverrideEmail: builder.mutation<
      any,
      { id: string; emailId: string }
    >({
      query: ({ id, emailId }) => ({
        url: `/${id}/override/email/${emailId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    deleteOverridePhone: builder.mutation<
      any,
      { id: string; phoneId: string }
    >({
      query: ({ id, phoneId }) => ({
        url: `/${id}/override/phone/${phoneId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    setOverrideEmailPrimary: builder.mutation<
      any,
      { id: string; emailId: string }
    >({
      query: ({ id, emailId }) => ({
        url: `/${id}/override/email/${emailId}/primary`,
        method: "PATCH",
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    setOverridePhonePrimary: builder.mutation<
      any,
      { id: string; phoneId: string }
    >({
      query: ({ id, phoneId }) => ({
        url: `/${id}/override/phone/${phoneId}/primary`,
        method: "PATCH",
      }),
      invalidatesTags: [{ type: "Person" }],
    }),
    connectPerson: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}/connect`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Person" }, { type: "Persons" }],
    }),
  }),
});

export const {
  useGetPersonsQuery,
  useLazyGetPersonsQuery,
  useLazyGetPersonByIdQuery,
  useGetPersonByIdQuery,
  useGetSuggestedPersonsQuery,
  useGetPersonsCountQuery,
  useGetPersonsCountByTypeQuery,
  useGetBucketCountsQuery,
  useUpdatePersonByIdMutation,
  useCreateRelationshipMutation,
  useSetPersonLeadMutation,
  useBulkImportPersonsMutation,
  useCreatePersonPhoneNumberMutation,
  useUpdatePersonPhoneNumberMutation,
  useDeletePersonPhoneNumberMutation,
  useCreatePersonEmailMutation,
  useUpdatePersonEmailMutation,
  useCreatePersonNoteMutation,
  useGetPersonNotesQuery,
  useDeleteNoteMutation,
  useUpdateNoteMutation,
  useCreatePersonRatingMutation,
  useUpdatePersonRatingMutation,
  useDeleteRatingMutation,
  useDeletePersonMutation,
  useCreatePersonAddressMutation,
  useUpdatePersonAddressMutation,
  useDeletePersonAddressMutation,
  useDismissPersonProfileBannerMutation,
  useUpdatePersonOverrideMutation,
  useAddOverrideEmailMutation,
  useAddOverridePhoneMutation,
  useDeleteOverrideEmailMutation,
  useDeleteOverridePhoneMutation,
  useSetOverrideEmailPrimaryMutation,
  useSetOverridePhonePrimaryMutation,
  useConnectPersonMutation,
} = personsApi;
